<script setup lang="ts" generic="T extends IMapPoint">
import type { IMapPoint } from "@/components/ui/map/map.types";
import type { TSite } from "@/data/api/kili/sites/types/sites.type";

import { onClickOutside } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch } from "vue";
import { InfoWindow } from "vue3-google-map";

import ToolIcon from "@/assets/icons/tool.svg?component";
import { hasSpiderfyOffset, type IMapMarkerOffset } from "@/components/ui/map/map-spiderfy";
import {
  formatGeolocatedAddressCity,
  formatGeolocatedAddressStreet,
  formatPostalAddressCity,
  formatPostalAddressStreet,
  hasPostalAddress,
} from "@/utils/address-utils";

import ColoredBadge from "@/components/ui/colored-badge.vue";
import MapDefaultMarker from "@/components/ui/map/map-default-marker.vue";

interface IProps {
  site: TSite & T;
  spiderfyOffset?: IMapMarkerOffset;
}

interface IEmits {
  (e: "click:marker", payload: T): void;
  (e: "click:programming", payload: T): void;
}

defineOptions({ inheritAttrs: false });
const props = defineProps<IProps>();
const emit = defineEmits<IEmits>();

const infoWindowContentId = ref(`iwc-${props.site.siteId}`);

const infoWindowDialogElement = useTemplateRef("infoWindowDialogElement");
const isInfoWindowOpened = ref(false);

const infoWindowPixelOffset = computed(() => {
  const { x = 0, y = 0 } = props.spiderfyOffset ?? {};
  return new google.maps.Size(x, y);
});

function handleInfoWindowClosed() {
  isInfoWindowOpened.value = false;
}

function handleMarkerClicked() {
  isInfoWindowOpened.value = true;
  emit("click:marker", props.site);
}

const streetAddress = computed<string>(() => {
  return hasPostalAddress(props.site.siteAddressDto)
    ? formatPostalAddressStreet(props.site.siteAddressDto.postalSiteAddress)
    : formatGeolocatedAddressStreet(props.site.siteAddressDto.geolocatedSiteAddress);
});

const cityAddress = computed<string>(() => {
  return hasPostalAddress(props.site.siteAddressDto)
    ? formatPostalAddressCity(props.site.siteAddressDto.postalSiteAddress)
    : formatGeolocatedAddressCity(props.site.siteAddressDto.geolocatedSiteAddress);
});

onClickOutside(infoWindowDialogElement, () => {
  if (isInfoWindowOpened.value) {
    handleInfoWindowClosed();
  }
});

watch(
  () => props.spiderfyOffset,
  (offset, previousOffset) => {
    if (hasSpiderfyOffset(previousOffset) && !hasSpiderfyOffset(offset)) {
      handleInfoWindowClosed();
    }
  },
);
</script>

<template>
  <MapDefaultMarker
    v-bind="$attrs"
    :background-color="props.site.programingIsSuspended ? 'var(--color-warning-subtle)' : undefined"
    :container-class="{
      '!text-warning-strong': props.site.programingIsSuspended,
    }"
    :marker="props.site"
    :spiderfy-offset="props.spiderfyOffset"
    :visible="!isInfoWindowOpened"
    @click="handleMarkerClicked"
  >
    <template #label>
      <ToolIcon
        v-if="props.site.programingIsSuspended"
        class="inline-block mr-0.5"
      />
      {{ props.site.name }}
    </template>
  </MapDefaultMarker>
  <InfoWindow
    v-if="isInfoWindowOpened"
    :id="infoWindowContentId"
    ref="infoWindowDialogElement"
    v-model="isInfoWindowOpened"
    :options="{
      position: { lat: site.lat, lng: site.lng },
      headerDisabled: true,
      pixelOffset: infoWindowPixelOffset,
    }"
  >
    <div
      class="bg-white flex flex-col gap-4 w-full cursor-pointer"
      @click="$emit('click:programming', props.site)"
    >
      <div class="flex w-full truncate text-ellipsis flex-row items-center justify-start gap-4">
        <ColoredBadge
          class="shrink-0"
          :name="props.site.name"
        />

        <div class="flex w-full truncate flex-col gap-0">
          <h3 class="cursor-pointer w-fit text-base font-semibold">
            {{ props.site.name }}
          </h3>

          <address class="flex flex-col text-text-secondary not-italic">
            <span>{{ streetAddress }}</span>
            <span>{{ cityAddress }}</span>
          </address>
        </div>
      </div>
    </div>
  </InfoWindow>
</template>
